export type Marketplace = {
  amazonUrl?: string;
  flipkartUrl?: string;
};

export type ProductImage = {
  id: number;
  url: string;
  alt: string;
  width?: number;
  height?: number;
};

/** A purchasable canonical SKU returned inside a public product family. */
export type StorefrontVariant = {
  id: string;
  sku: string;
  cxFacingName?: string | null;
  title: string;
  variantLabel?: string | null;
  /** Ordered customer-facing values supplied by the public family contract when available. */
  options?: string[];
  price: number;
  mrp?: number | null;
  availability: "in_stock" | "out_of_stock";
  images: ProductImage[];
  marketplace: Marketplace;
};

export type StorefrontCategory = {
  code: string;
  name: string;
  subcategory?: { code: string; name: string } | null;
};

/** A shopper filter value: a product type (three-letter manufacturing category) or a theme. */
export type StorefrontFilterValue = { code: string; name: string };

/** A non-purchasable catalogue card and its SKU variants. */
export type StorefrontFamily = {
  id: string;
  familyId: number;
  prodUnique: string;
  cxFacingName?: string | null;
  title?: string | null;
  description?: string | null;
  shortDescription?: string | null;
  seo?: { title?: string; description?: string };
  category?: StorefrontCategory | null;
  defaultVariantId?: string | null;
  defaultImage?: ProductImage | null;
  variants: StorefrontVariant[];
  /** What the family's designs are, for the shop filters (families-v1; absent from older responses). */
  types?: StorefrontFilterValue[];
  themes?: StorefrontFilterValue[];
};

export type CartLine = {
  /** This remains a SKU, never a family, so checkout is canonical. */
  product: StorefrontVariant;
  quantity: number;
};

export type PaymentMethod = "RAZORPAY" | "COD";

export type StorefrontSettings = {
  freeShippingThreshold: number;
  underThresholdShipping: number | null;
  gstRate?: number | null;
  pricesIncludeGst?: boolean;
  /** Offered by the storefront API for this shopper; the client never assumes online payment. */
  paymentMethods?: PaymentMethod[];
};

/** Seller of record snapshotted onto the order: trade name, legal entity and GSTIN. */
export type OrderSeller = { tradeName: string | null; legalName: string; gstin: string; gstStateCode: string | null; registeredAddress: string | null };

export type CatalogueResponse = {
  families: StorefrontFamily[];
  /** Favourite family ids in order (emAdmin → Storefronts → Favourites); only listed families. */
  featured?: string[];
  preview: boolean;
  settings?: StorefrontSettings;
  contractVersion?: "families-v1";
};

export type FamilyResponse = {
  family: StorefrontFamily;
  preview: boolean;
  contractVersion?: "families-v1";
};

export type Customer = { id: number; email: string; name: string; phone?: string | null };
export type CustomerSession = { customerId: number | null; csrf: string };
export type CustomerAddress = {
  id: number;
  recipientName: string;
  phone: string;
  addressLine1: string;
  addressLine2?: string | null;
  city: string;
  state: string;
  pin: string;
  isDefault: boolean;
};
export type CustomerOrder = {
  orderNumber: string;
  total: number;
  currency: "INR";
  paymentStatus: string;
  orderStatus: string;
  createdAt: string;
  itemSummary?: string | null;
};
export type CustomerOrderDetail = CustomerOrder & {
  customerName: string;
  email: string;
  phone: string;
  address: { address?: string; city?: string; state?: string; pin?: string };
  subtotal: number;
  shipping: number;
  tax: number | null;
  paymentMethod: string;
  items: Array<{ productId: string; title: string; quantity: number; unitPrice: number; subtotal: number; gstRate: number | null }>;
  seller?: OrderSeller | null;
};

export function customerVariantName(variant: StorefrontVariant): string {
  return variant.cxFacingName?.trim() || variant.title;
}

export function customerFamilyName(family: StorefrontFamily): string | null {
  return family.cxFacingName?.trim() || family.title?.trim() || null;
}
